import { useTranslation } from "@repo/i18n";
import { formatCountdown } from "../format";
import { useHoldCountdown } from "../hooks/useHoldCountdown";

/**
 * The visible pre-submit hold countdown. The per-second `m:ss` tick is `aria-hidden` so a screen
 * reader is NOT read to every second; a separate visually-hidden `role="status" aria-live="polite"`
 * region carries the threshold announcements (60/30/10 s) only (ADR-0023 accessible countdown).
 * Remaining time is derived from the server `expiresAt` (§D.4), correct after a tab sleep, and expiry
 * fires `onExpire` once — the caller swaps to fresh alternatives (R7).
 */
export function HoldCountdown({
  expiresAtSec,
  onExpire,
}: {
  expiresAtSec: number;
  onExpire: () => void;
}) {
  const { t } = useTranslation();
  const { remainingSec, announce } = useHoldCountdown(expiresAtSec, onExpire);
  const announceText =
    announce !== null && announce > 0
      ? t("widget.booking.hold.announce", { seconds: announce })
      : "";

  return (
    <div className="rsv-countdown">
      <span className="rsv-hint">{t("widget.booking.hold.label")}</span>
      <span className="rsv-countdown-value" aria-hidden="true" data-testid="hold-countdown">
        {formatCountdown(remainingSec)}
      </span>
      <output className="sr-only" aria-live="polite">
        {announceText}
      </output>
    </div>
  );
}
